// The read-your-writes fence's client-side token store (the
// state-distribution rywf pilot, docs/state-distribution/rywf-fence-pilot/
// SCOPE.md — "FE BEHAVIOR slice"). Mutations mint opaque `writeToken`s; a
// fence-aware read presents one back and the response DISCLOSES whether the
// answering projections converged past it. This module owns the client's
// half of that loop: which tokens are live, what each token's last
// disclosure said, and the classification rules — transport-agnostic, so
// the Apollo wiring stays thin links beside the client (the
// `@shuttlerock/observability` precedent).
//
// Deliberately header-shaped: in the F2 header world the store is
// unchanged — the present link writes `presentations()` into a request
// header instead of a per-field argument. Homed here per
// placement-follows-demand: the pilot's one lane is the only consumer; the
// F2 estate lane re-homes it when a second consumer exists.

/**
 * One fence-aware answer's convergence disclosure, as served in the
 * response (`ConvergenceMeta`'s claim fields). `converged` is a POSITIVE
 * claim only: with `fenced: false` the answer claims nothing — it never
 * means stale and never means denied.
 */
export type FenceConvergenceClaim = {
  fenced: boolean;
  converged: boolean;
};

/**
 * What one response's disclosure set adds up to, most-conservative first:
 *
 * - `pending` — some fenced leg did NOT converge: a disclosed miss. The
 *   answer may predate the caller's write (the P6 pending posture — never
 *   render it as a settled answer).
 * - `converged` — a fenced leg converged and none missed: the answer
 *   provably happens-after the covered writes.
 * - `unfenced` — the surface answered but held no fence (no token
 *   presented, or the server degraded — a rotated key, a malformed
 *   token): no claim either way, the read behaved as legacy.
 * - `silent` — the response carried no disclosure at all: nothing was
 *   learned (an errored or disclosure-less response must not be mistaken
 *   for a degradation verdict).
 */
export type FenceReadOutcome = "pending" | "converged" | "unfenced" | "silent";

/**
 * The pending predicate and its aggregation over a response's disclosures.
 * Pending iff `fenced && !converged` on any leg; `fenced: false` legs are
 * "legacy settled" — they never arm pending and never claim convergence.
 */
export const classifyFenceDisclosures = (
  claims: readonly FenceConvergenceClaim[],
): FenceReadOutcome => {
  if (claims.length === 0) {
    return "silent";
  }
  if (claims.some((claim) => claim.fenced && !claim.converged)) {
    return "pending";
  }
  if (claims.some((claim) => claim.fenced && claim.converged)) {
    return "converged";
  }
  return "unfenced";
};

/**
 * How a stored token stands with respect to disclosures seen so far.
 * `captured` = no disclosure yet; `pending` = last disclosure was a miss;
 * `converged` = a disclosure claimed convergence (sticky — projections are
 * monotone, so a later out-of-order miss can never un-converge a token);
 * `unverifiable` = a read presenting it came back unfenced, so the server
 * cannot evaluate it (never presented again — re-presenting only re-warns).
 */
export type FenceTokenState = "captured" | "pending" | "converged" | "unverifiable";

export type FenceCapture = {
  /** The opaque `writeToken`, verbatim — never parsed client-side. */
  token: string;
  /**
   * Id of the entity the minting payload returned beside the token — the
   * grain a caller's "did MY write land" question is asked at. Supplied
   * by the capture layer's payload readers (this platform module never
   * learns a payload family's shape); absent when the payload returned
   * no entity.
   */
  entityId?: string;
  /**
   * Ids of the containers whose reads this token should fence — supplied
   * by the capture layer's payload readers. Empty = scope-unknown: the
   * payload named no readable containers, and the arg-era presentation
   * treats such a token as presentable anywhere rather than nowhere. The
   * F2 header presents every live token and this narrowing disappears.
   */
  scopeIds?: readonly string[];
  /**
   * The minting response's own disclosure (a payload that selects its
   * entity's `viewerOperationChecks { convergence }` learns at mutation
   * time whether the write already converged — the self-fenced signal).
   */
  outcome?: FenceReadOutcome;
};

export type FencePresentation = {
  token: string;
  scopeIds: readonly string[];
};

type FenceTokenRecord = {
  token: string;
  entityId?: string;
  scopeIds: readonly string[];
  capturedAt: number;
  state: FenceTokenState;
};

/**
 * A token's TTL. Over-retention is nearly free (a converged token probes
 * and passes instantly server-side); under-retention degrades the read to
 * exactly legacy behavior — so the window only needs to comfortably cover
 * the post-write re-read spans it exists for.
 */
export const FENCE_TOKEN_TTL_MS = 20_000;

/** How many tokens the store retains; beyond it the oldest is dropped. */
export const FENCE_STORE_CAPACITY = 4;

export type FenceStoreOptions = {
  ttlMs?: number;
  capacity?: number;
  now?: () => number;
};

export type FenceStore = {
  /** Record a minted token (idempotent per token string). */
  capture: (capture: FenceCapture) => void;
  /**
   * The live tokens a request may present, newest-first: unexpired and
   * not `unverifiable` (converged tokens stay presentable — retention is
   * what keeps a follow-up read read-your-writes within the window).
   */
  presentations: () => FencePresentation[];
  /** Fold one read's classified disclosure into the presented token. */
  recordDisclosure: (token: string, outcome: FenceReadOutcome) => void;
  /**
   * The fence's answer to "has the write that returned this entity been
   * verified applied": `converged` / `pending` from the entity's live
   * token state; `none` when the store holds no live claim for it (no
   * token, expired, evicted, or unverifiable) — the caller behaves as
   * legacy.
   */
  entityOutcome: (entityId: string) => "pending" | "converged" | "none";
  /** Drop everything (tests, sign-out). */
  reset: () => void;
};

export const createFenceStore = ({
  ttlMs = FENCE_TOKEN_TTL_MS,
  capacity = FENCE_STORE_CAPACITY,
  now = Date.now,
}: FenceStoreOptions = {}): FenceStore => {
  // Newest last; pruned lazily on every access.
  let records: FenceTokenRecord[] = [];

  const prune = () => {
    const cutoff = now() - ttlMs;
    records = records.filter((record) => record.capturedAt > cutoff);
  };

  const stateForOutcome = (outcome: FenceReadOutcome): FenceTokenState | undefined => {
    switch (outcome) {
      case "converged":
        return "converged";
      case "pending":
        return "pending";
      case "unfenced":
        return "unverifiable";
      case "silent":
        return undefined;
    }
  };

  const applyOutcome = (record: FenceTokenRecord, outcome: FenceReadOutcome) => {
    const state = stateForOutcome(outcome);
    if (!state) {
      return;
    }
    // Convergence is sticky: projections only advance, so a stale or
    // out-of-order disclosure can never demote a converged token.
    if (record.state === "converged") {
      return;
    }
    record.state = state;
  };

  return {
    capture: ({ token, entityId, scopeIds = [], outcome }) => {
      if (!token) {
        return;
      }
      prune();
      const existing = records.find((record) => record.token === token);
      if (existing) {
        if (outcome) {
          applyOutcome(existing, outcome);
        }
        return;
      }
      const record: FenceTokenRecord = {
        token,
        entityId,
        scopeIds,
        capturedAt: now(),
        state: "captured",
      };
      if (outcome) {
        applyOutcome(record, outcome);
      }
      records.push(record);
      if (records.length > capacity) {
        records = records.slice(records.length - capacity);
      }
    },

    presentations: () => {
      prune();
      return records
        .filter((record) => record.state !== "unverifiable")
        .map((record) => ({ token: record.token, scopeIds: record.scopeIds }))
        .reverse();
    },

    recordDisclosure: (token, outcome) => {
      prune();
      const record = records.find((candidate) => candidate.token === token);
      if (!record) {
        return;
      }
      applyOutcome(record, outcome);
    },

    entityOutcome: (entityId) => {
      prune();
      // Newest wins if several captures name one entity (a re-minted
      // token supersedes its predecessor's claim surface).
      for (let index = records.length - 1; index >= 0; index -= 1) {
        const record = records[index];
        if (record.entityId !== entityId) {
          continue;
        }
        if (record.state === "converged") {
          return "converged";
        }
        if (record.state === "pending") {
          return "pending";
        }
        return "none";
      }
      return "none";
    },

    reset: () => {
      records = [];
    },
  };
};

/**
 * The per-tab store instance every consumer shares: the Apollo links write
 * it (capture/disclosure) and read it (presentation); behavior code asks
 * it `entityOutcome`. Per-tab and in-memory on purpose — a token is a
 * causal anchor for THIS session's own writes, never durable state.
 */
export const fenceStore = createFenceStore();
